import React, { memo } from "react";
import { Box } from "@mui/material";
import { GREY } from "../../../../../utils/constants/colors";
import { fontWeight, ICON_LABELS } from "../../../../../utils/constants/UI";
import TypographyInline from "../../../../../components/Typography/TypographyInline";
import InputField from "../../../../../components/InputFields/InputField";
import { Button } from "../../../../../components/Buttons";
import { AutoCompleteCustom } from "../../../../../components/Autocomplete/AutoCompleteCustom";
import { sharedBillingStyles as s } from "./billingUtils";

/**
 * BillingPaymentSection
 *
 * Hosts Billing Address form and Secondary Email form.
 * Payment Methods section is preserved as a commented-out block
 * per original code.
 */
const BillingPaymentSection = memo(
  ({
    billingAddress,
    setBillingAddress,
    secondaryEmail,
    setSecondaryEmail,
    handleSaveAddress,
    handleSaveEmail,
    isSavingAddress,
    isSavingEmail,
    filteredCountries,
    handleCountrySearch,
  }) => {
    const setField = (field) => (e) =>
      setBillingAddress((prev) => ({ ...prev, [field]: e.target.value }));

    return (
      <Box style={s.contentWrapper}>
        {/* Payment Methods Section - Hidden per user request */}
        {/*
        <Box style={s.card}>
          <Box style={s.cardHeader}>
            <TypographyInline
              size="md"
              fontWeight={fontWeight.SEMIBOLD}
              color={GREY.PRIMARY}
              label="Payment Methods"
            />
            <Box style={{ width: "fit-content" }}>
              <Button
                label="Add Card"
                size="sm36"
                variant="primary"
                LeftIcon={ICON_LABELS.ADD}
              />
            </Box>
          </Box>
          ...
        </Box>
        */}

        <Box
          style={{ display: "grid", gap: "24px" }}
          sx={{
            gridTemplateColumns: {
              xs: "1fr",
              xl: "1fr 1fr",
            },
          }}
        >
          {/* Billing Address */}
          <Box style={s.card}>
            <Box style={{ marginBottom: "16px" }}>
              <TypographyInline
                size="md"
                fontWeight={fontWeight.SEMIBOLD}
                color={GREY.PRIMARY}
                label="Billing Address"
              />
              <Box style={{ marginTop: "4px" }}>
                <TypographyInline
                  size="xs"
                  color={GREY.TERTIARY}
                  label="This information will appear on your monthly invoices."
                />
              </Box>
            </Box>

            <Box
              style={{ display: "flex", flexDirection: "column", gap: "11px" }}
            >
              <FormField label="COMPANY NAME">
                <InputField
                  size="sm36"
                  placeholder="Acme Inc."
                  value={billingAddress.company_name}
                  onChange={setField("company_name")}
                />
              </FormField>

              <FormField label="ADDRESS LINE 1">
                <InputField
                  size="sm36"
                  placeholder="123 Market St"
                  value={billingAddress.address_line1}
                  onChange={setField("address_line1")}
                />
              </FormField>

              <FormField label="ADDRESS LINE 2 (OPTIONAL)">
                <InputField
                  size="sm36"
                  placeholder="Suite 400"
                  value={billingAddress.address_line2}
                  onChange={setField("address_line2")}
                />
              </FormField>

              <Box
                style={{
                  display: "grid",
                  gap: "12px",
                }}
                sx={{
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                  },
                }}
              >
                <FormField label="CITY">
                  <InputField
                    size="sm36"
                    placeholder="San Francisco"
                    value={billingAddress.city}
                    onChange={setField("city")}
                  />
                </FormField>
                <FormField label="STATE / PROVINCE">
                  <InputField
                    size="sm36"
                    placeholder="California"
                    value={billingAddress.state}
                    onChange={setField("state")}
                  />
                </FormField>
              </Box>

              <Box
                style={{
                  display: "grid",
                  gap: "12px",
                }}
                sx={{
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                  },
                }}
              >
                <FormField label="POSTAL CODE">
                  <InputField
                    size="sm36"
                    placeholder="94105"
                    value={billingAddress.postal_code}
                    onChange={setField("postal_code")}
                  />
                </FormField>
                <FormField label="COUNTRY">
                  <AutoCompleteCustom
                    size="sm36"
                    placeholder="Select Country"
                    items={filteredCountries}
                    valueState={
                      billingAddress.country ? [billingAddress.country] : []
                    }
                    idState={
                      billingAddress.country ? [billingAddress.country] : []
                    }
                    setValueState={(val) =>
                      setBillingAddress((prev) => ({
                        ...prev,
                        country: val[0] || "",
                      }))
                    }
                    setIdState={() => {}}
                    isSearchEnable={true}
                    handleSearch={handleCountrySearch}
                  />
                </FormField>
              </Box>

              <Box style={{ marginTop: "10px", width: "fit-content" }}>
                <Button
                  label="Update Billing Address"
                  variant="primary"
                  size="sm36"
                  onClick={handleSaveAddress}
                  isLoading={isSavingAddress}
                />
              </Box>
            </Box>
          </Box>

          {/* Secondary Billing Email */}
          <Box style={s.card}>
            <Box style={{ marginBottom: "16px" }}>
              <TypographyInline
                size="md"
                fontWeight={fontWeight.SEMIBOLD}
                color={GREY.PRIMARY}
                label="Secondary Billing Email"
              />
              <Box style={{ marginTop: "4px" }}>
                <TypographyInline
                  size="xs"
                  color={GREY.TERTIARY}
                  label="Invoices will be sent to your account email and this address."
                />
              </Box>
            </Box>

            <Box
              style={{ display: "flex", flexDirection: "column", gap: "11px" }}
            >
              <FormField label="SECONDARY EMAIL ADDRESS">
                <InputField
                  size="sm36"
                  placeholder="billing@company.com"
                  value={secondaryEmail}
                  onChange={(e) => setSecondaryEmail(e.target.value)}
                  LeftIcon={ICON_LABELS.EMAIL}
                />
              </FormField>

              <Box style={{ marginTop: "10px", width: "fit-content" }}>
                <Button
                  label="Update Billing Email"
                  variant="primary"
                  size="sm36"
                  onClick={handleSaveEmail}
                  isLoading={isSavingEmail}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  },
);

// ─── Local helper: form field wrapper ─────────────────────────────────────────
const FormField = ({ label, children }) => (
  <Box style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
    <TypographyInline
      size="xs"
      color={GREY.TERTIARY}
      fontWeight={fontWeight.MEDIUM}
      label={label}
    />
    {children}
  </Box>
);

BillingPaymentSection.displayName = "BillingPaymentSection";

export default BillingPaymentSection;
